import { formatMoney, type OfferSnapshot, type PublicShareDto, REJECTION_REASONS, type SnapshotVariant } from '@bqcrm/shared';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Collapse,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  Radio,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconFileTypePdf } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useParams } from 'react-router';
import { api, errorText, HttpError } from '../api';
import { dayLong, qtyPl } from '../format';

/** Dzień słownie bez dnia tygodnia: „5 marca 2026”. */
const date = (iso: string) => dayLong(iso, { weekday: false });

function VariantCard({
  v,
  snap,
  accepted,
  canAccept,
  onAccept,
  decisions,
}: {
  v: SnapshotVariant;
  snap: OfferSnapshot;
  accepted: boolean;
  canAccept: boolean;
  onAccept: () => void;
  /** „Poproś o zmiany” i „Odrzuć” obok akceptacji (oferta z jednym wariantem — QA P15) */
  decisions?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const withPrices = v.presentation === 'szczegolowa';
  return (
    <Card withBorder radius="md" padding="lg" style={accepted ? { borderColor: 'var(--mantine-color-green-6)', borderWidth: 2 } : undefined}>
      <Group justify="space-between" align="flex-start">
        <Title order={4}>{v.name}</Title>
        {accepted && (
          <Badge color="green" leftSection={<IconCheck size={12} />}>
            Zaakceptowany
          </Badge>
        )}
      </Group>
      {v.description && (
        <Text size="sm" c="dimmed" mt={4}>
          {v.description}
        </Text>
      )}
      <Text fz={28} fw={700} mt="md" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(v.totals.grossGr)}
      </Text>
      <Text size="xs" c="dimmed">
        brutto · netto {formatMoney(v.totals.netGr)} + VAT {formatMoney(v.totals.vatGr)}
      </Text>
      <Button variant="subtle" size="xs" px={0} mt="sm" onClick={() => setOpen((o) => !o)}>
        {open ? 'Ukryj szczegóły' : 'Pokaż, co obejmuje'}
      </Button>
      <Collapse expanded={open}>
        <Table mt="xs" verticalSpacing={4}>
          <Table.Tbody>
            {v.items.map((i) => (
              <Table.Tr key={i.id}>
                <Table.Td>
                  <Text size="sm">{i.name}</Text>
                  {i.components.length > 0 && (
                    <Text size="xs" c="dimmed">
                      W skład wchodzi: {i.components.map((c) => c.name).join(', ')}
                    </Text>
                  )}
                  {i.description && (
                    <Text size="xs" c="dimmed">
                      {i.description}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                  <Text size="sm">
                    {qtyPl(i.quantity)} {i.unit}
                  </Text>
                </Table.Td>
                {withPrices && (
                  <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                    <Text size="sm">{formatMoney(i.netGr)}</Text>
                    <Text size="xs" c="dimmed">
                      netto, VAT {i.vatRate}%
                    </Text>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {!withPrices && (
          <Text size="xs" c="dimmed" mt={4}>
            Cena obejmuje całość wymienionego zakresu.
          </Text>
        )}
      </Collapse>
      {canAccept && (
        <Button fullWidth mt="lg" size="md" onClick={onAccept} color={snap.company.color}>
          Akceptuję ten wariant
        </Button>
      )}
      {canAccept && decisions}
    </Card>
  );
}

function AcceptDialog({
  token,
  offer,
  variant,
  onClose,
  onDone,
  onReject,
}: {
  token: string;
  offer: PublicShareDto['offers'][number];
  variant: SnapshotVariant;
  onClose: () => void;
  onDone: () => void;
  onReject: () => void;
}) {
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [codeSent, setCodeSent] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const snap = offer.snapshot;
  const base = { versionId: offer.versionId, variantId: variant.id, personName: name };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/public/offers/${token}/accept/confirm`, { method: 'POST', body: { ...base, code, consent } });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ otpRequired: boolean; emailMasked: string | null }>(`/public/offers/${token}/accept/request`, { method: 'POST', body: base });
      if (r.otpRequired) setCodeSent(r.emailMasked);
      else await confirm();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title="Potwierdzenie akceptacji" size="md">
      <Stack>
        <Alert color="blue" variant="light">
          Akceptuję ofertę <b>{snap.number}</b>, wariant <b>„{variant.name}”</b>, cena <b>{formatMoney(variant.totals.grossGr)} brutto</b>, wystawioną przez{' '}
          {snap.company.fullName}.
        </Alert>
        <TextInput label="Imię i nazwisko" value={name} onChange={(e) => setName(e.currentTarget.value)} disabled={!!codeSent} data-autofocus />
        <Checkbox
          checked={consent}
          onChange={(e) => setConsent(e.currentTarget.checked)}
          disabled={!!codeSent}
          label="Zapoznałem(-am) się z treścią oferty. Rozumiem, że akceptacja oferty nie jest zawarciem umowy — umowa zostanie przygotowana do podpisu."
        />
        {codeSent && (
          <>
            <Text size="sm">
              Wysłaliśmy 6-cyfrowy kod na adres <b>{codeSent}</b>. Kod jest ważny 15 minut.
            </Text>
            <TextInput label="Kod z e-maila" value={code} onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" />
          </>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertTriangle />}>
            {error}
          </Alert>
        )}
        <Group justify="space-between">
          {codeSent ? (
            <Button variant="subtle" size="xs" onClick={() => void start()} disabled={busy}>
              Wyślij kod ponownie
            </Button>
          ) : (
            <Button variant="subtle" color="red" size="xs" onClick={onReject}>
              Odrzuć ofertę
            </Button>
          )}
          <Group>
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            {codeSent ? (
              <Button onClick={() => void confirm()} loading={busy} disabled={code.length !== 6}>
                Potwierdzam akceptację
              </Button>
            ) : (
              <Button onClick={() => void start()} loading={busy} disabled={name.trim().length < 3 || !consent}>
                Dalej
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Odrzucenie oferty przez klienta — z powodem (pomaga przygotować lepszą ofertę). */
function RejectDialog({ token, offer, onClose, onDone }: { token: string; offer: PublicShareDto['offers'][number]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/public/offers/${token}/reject`, { method: 'POST', body: { versionId: offer.versionId, personName: name, reasonCode, reason } });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal opened onClose={onClose} title="Odrzucenie oferty" size="md">
      <Stack>
        <Text size="sm">
          Odrzucasz ofertę <b>{offer.snapshot.number}</b> ({offer.snapshot.company.fullName}). Po odrzuceniu tej wersji nie będzie można zaakceptować — jeśli zechcesz, przygotujemy
          nową.
        </Text>
        <TextInput label="Imię i nazwisko" value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        <Radio.Group label="Powód" value={reasonCode} onChange={setReasonCode}>
          <Stack gap={6} mt={6}>
            {Object.entries(REJECTION_REASONS).map(([value, label]) => (
              <Radio key={value} value={value} label={label} />
            ))}
          </Stack>
        </Radio.Group>
        <Textarea
          label={reasonCode === 'inny' ? 'Opisz powód' : 'Uwagi (opcjonalnie)'}
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {error && (
          <Alert color="red" icon={<IconAlertTriangle />}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            color="red"
            onClick={() => void submit()}
            loading={busy}
            disabled={name.trim().length < 3 || !reasonCode || (reasonCode === 'inny' && reason.trim().length < 3)}
          >
            Odrzucam ofertę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Prośba o zmiany (QA P15) — zamiast odrzucenia: firma przygotuje nową wersję oferty. */
function ChangesDialog({ token, offer, onClose, onDone }: { token: string; offer: PublicShareDto['offers'][number]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/public/offers/${token}/changes`, { method: 'POST', body: { versionId: offer.versionId, personName: name, note } });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal opened onClose={onClose} title="Prośba o zmiany w ofercie" size="md">
      <Stack>
        <Text size="sm">
          Napisz, co chcesz zmienić w ofercie <b>{offer.snapshot.number}</b> (np. inne urządzenie, zakres, termin). Przygotujemy nową wersję oferty i prześlemy ją e-mailem.
        </Text>
        <TextInput label="Imię i nazwisko" value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        <Textarea label="Co zmienić?" autosize minRows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        {error && (
          <Alert color="red" icon={<IconAlertTriangle />}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => void submit()} loading={busy} disabled={name.trim().length < 3 || note.trim().length < 3}>
            Wyślij prośbę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const STATUS_INFO: Record<string, { color: string; text: string }> = {
  zastapiona: { color: 'gray', text: 'Ta wersja oferty została zastąpiona nowszą. Skontaktuj się z nami, aby otrzymać aktualną ofertę.' },
  wygasla: { color: 'orange', text: 'Termin ważności tej oferty minął. Skontaktuj się z nami, aby ją odnowić.' },
  wycofana: { color: 'red', text: 'Ta oferta została wycofana.' },
  // QA P5: sprawa zamknięta
  nieaktualna: { color: 'gray', text: 'Oferta nieaktualna — skontaktuj się z nami.' },
};
// odrzucona: komunikat ze szczegółami poniżej wariantów

/** Strona klienta pod bezpiecznym linkiem — bez logowania, tylko oferty z linku. */
export function PublicOfferPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['public-offer', token], queryFn: () => api<PublicShareDto>(`/public/offers/${token}`), retry: false, refetchOnWindowFocus: false });
  const [accepting, setAccepting] = useState<{ offer: PublicShareDto['offers'][number]; variant: SnapshotVariant } | null>(null);
  const [done, setDone] = useState(false);
  const [rejecting, setRejecting] = useState<PublicShareDto['offers'][number] | null>(null);
  const [changing, setChanging] = useState<PublicShareDto['offers'][number] | null>(null);
  /** QA P15: „Poproś o zmiany” i „Odrzucam ofertę” obok akceptacji */
  const decisionButtons = (o: PublicShareDto['offers'][number]) => (
    <>
      <Button variant="light" onClick={() => setChanging(o)}>
        Poproś o zmiany
      </Button>
      <Button variant="outline" color="red" onClick={() => setRejecting(o)}>
        Odrzucam ofertę
      </Button>
    </>
  );

  if (q.isLoading)
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  if (q.error)
    return (
      <Container size="sm" py={80}>
        <Alert color={q.error instanceof HttpError && q.error.status === 410 ? 'orange' : 'red'} title="Oferta niedostępna" icon={<IconAlertTriangle />}>
          {errorText(q.error)}
        </Alert>
      </Container>
    );
  const data = q.data!;

  return (
    <Box bg="var(--app-subtle)" mih="100vh" pb="xl">
      {data.demo && (
        <Box bg="var(--app-tint-orange)" py={6}>
          <Text ta="center" size="sm" c="var(--app-text-orange-strong)">
            Środowisko demonstracyjne — dane i firmy są fikcyjne.
          </Text>
        </Box>
      )}
      <Container size="lg" pt="xl">
        {done && (
          <Alert color="green" icon={<IconCheck />} mb="lg" title="Dziękujemy — akceptacja została zapisana">
            Potwierdzenie wysłaliśmy e-mailem. Wkrótce skontaktujemy się w sprawie umowy.
          </Alert>
        )}
        <Stack gap={48}>
          {data.offers.map((o) => {
            const s = o.snapshot;
            const info = STATUS_INFO[o.status];
            return (
              <Box key={o.versionId} id={`oferta-${o.versionId}`}>
                <Box h={6} bg={s.company.color} style={{ borderRadius: 3 }} mb="md" />
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <div>
                    <Text fw={700} size="lg">
                      {s.company.fullName}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {s.company.address}
                      {s.company.nip ? ` · NIP ${s.company.nip}` : ''}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {[s.company.phone, s.company.email].filter(Boolean).join(' · ')}
                    </Text>
                  </div>
                  <Stack gap={4} align="flex-end">
                    <Text size="sm" c="dimmed">
                      Oferta {s.number}
                    </Text>
                    <Text size="sm">
                      z {date(s.issuedAt)}, ważna do <b>{date(s.validUntil)}</b>
                    </Text>
                    <Button
                      variant="default"
                      size="xs"
                      leftSection={<IconFileTypePdf size={16} />}
                      component="a"
                      href={`/api/public/offers/${token}/versions/${o.versionId}/pdf`}
                      target="_blank"
                    >
                      Pobierz PDF
                    </Button>
                  </Stack>
                </Group>
                <Title order={2} mt="lg">
                  {s.title}
                </Title>
                <Text c="dimmed">
                  Dla: {s.client.displayName} · miejsce realizacji: {s.site.address}
                </Text>
                {o.newerVersionId && !o.canAccept ? (
                  // QA P19: nowsza wersja udostępniona temu samemu odbiorcy — na tej samej stronie
                  <Alert color="blue" mt="md" title="Dostępna jest aktualna wersja oferty">
                    <Stack gap="xs" align="flex-start">
                      <Text size="sm">Ta wersja nie jest już aktualna. Zobacz nowszą wersję tej oferty.</Text>
                      <Button size="xs" onClick={() => document.getElementById(`oferta-${o.newerVersionId}`)?.scrollIntoView({ behavior: 'smooth' })}>
                        Przejdź do aktualnej wersji
                      </Button>
                    </Stack>
                  </Alert>
                ) : (
                  info && (
                    <Alert color={info.color} mt="md">
                      {info.text}
                    </Alert>
                  )
                )}
                {o.status === 'do_zmiany' && o.changes && (
                  <Alert color="blue" mt="md" title="Przekazano prośbę o zmiany">
                    {date(o.changes.at)}: „{o.changes.note}”. Przygotujemy nową wersję oferty i prześlemy ją e-mailem.
                  </Alert>
                )}
                {o.acceptance && (
                  <Alert color="green" mt="md" icon={<IconCheck />}>
                    Zaakceptowano wariant „{s.variants.find((v) => v.id === o.acceptance!.variantId)?.name}” — {o.acceptance.personName}, {date(o.acceptance.acceptedAt)}.
                  </Alert>
                )}
                {o.canAccept && s.variants.length > 1 && (
                  <Text mt="md" size="sm">
                    Prosimy o wybór jednego z wariantów.
                  </Text>
                )}
                <SimpleGrid cols={{ base: 1, sm: Math.min(s.variants.length, 3) }} mt="md">
                  {s.variants.map((v) => (
                    <VariantCard
                      key={v.id}
                      v={v}
                      snap={s}
                      accepted={o.acceptance?.variantId === v.id}
                      canAccept={o.canAccept}
                      onAccept={() => setAccepting({ offer: o, variant: v })}
                      decisions={
                        s.variants.length === 1 && (
                          <Group grow mt="xs" gap="xs">
                            {decisionButtons(o)}
                          </Group>
                        )
                      }
                    />
                  ))}
                </SimpleGrid>
                {o.rejection && (
                  <Alert color="orange" mt="md">
                    Oferta odrzucona — {o.rejection.personName}, {date(o.rejection.rejectedAt)} (powód: {o.rejection.reasonLabel}).{!o.newerVersionId && ' Jeśli chcesz otrzymać zmienioną ofertę, skontaktuj się z nami.'}
                  </Alert>
                )}
                {o.canAccept && s.variants.length > 1 && (
                  <Group mt="md" gap="xs">
                    <Text size="sm" c="dimmed">
                      Żaden wariant nie pasuje?
                    </Text>
                    {decisionButtons(o)}
                  </Group>
                )}
                {s.terms && (
                  <>
                    <Divider my="lg" label="Warunki" labelPosition="left" />
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {s.terms}
                    </Text>
                  </>
                )}
                {s.preparedBy.name && (
                  <Text size="sm" c="dimmed" mt="md">
                    Ofertę przygotował(a): {s.preparedBy.name}
                    {s.preparedBy.phone ? ` · ${s.preparedBy.phone}` : ''}
                    {s.preparedBy.email ? ` · ${s.preparedBy.email}` : ''}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
        <Text size="xs" c="dimmed" ta="center" mt={60}>
          Link jest ważny do {date(data.expiresAt)}. Akceptacja oferty nie jest zawarciem umowy.
        </Text>
      </Container>
      {changing && (
        <ChangesDialog
          token={token}
          offer={changing}
          onClose={() => setChanging(null)}
          onDone={() => {
            setChanging(null);
            void qc.invalidateQueries({ queryKey: ['public-offer', token] });
          }}
        />
      )}
      {rejecting && (
        <RejectDialog
          token={token}
          offer={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            void qc.invalidateQueries({ queryKey: ['public-offer', token] });
          }}
        />
      )}
      {accepting && (
        <AcceptDialog
          token={token}
          offer={accepting.offer}
          variant={accepting.variant}
          onReject={() => {
            setRejecting(accepting.offer);
            setAccepting(null);
          }}
          onClose={() => setAccepting(null)}
          onDone={() => {
            setAccepting(null);
            setDone(true);
            void qc.invalidateQueries({ queryKey: ['public-offer', token] });
          }}
        />
      )}
    </Box>
  );
}
